import toastr from 'react-hot-toast';
import { useQuery } from 'react-query';
import { fetchWrapper } from 'utils/fetchWrapper';
import { CACHE_AND_STALE_TIME } from 'constants/index';
import { useWorkspaceContext } from 'hooks/common/useWorkspaceContext';
import {
  FilterParameters,
  generateCustomFilter,
  generateFilter,
  generateFilterExpression,
  generatePaginationQuery,
  generateSortQuery,
  generateURL,
  PaginationQuery,
} from 'utilities/hooks/urlGeneration';
import { BoardsResponse } from 'types/Boards';
import { QueryOptions, SPARK_RESOURCE_TYPES } from 'constants/common';
import { SparkFilterType } from 'components/Providers/ContextProviders/SparkSearchContextProviderV2';
import { useCurrentUser } from 'hooks/common';

const getBoards = async (
  variables?: MutationVariables,
  currentWorkspaceID?: string | null,
  currentUserID?: string | null
) => {
  const queryFilters: FilterParameters[] = [];
  const { filters, pagination } = variables || {};

  const { search, selectedItemType } = filters || {};

  if (search) {
    queryFilters.push({
      key: 'name',
      value: search,
      type: 'contains',
    });
  }

  if (selectedItemType === 'workspace' && currentWorkspaceID) {
    queryFilters.push({
      key: 'workspaceID',
      value: currentWorkspaceID,
      type: 'equal',
    });
  }

  if (selectedItemType === 'my-boards' && currentUserID) {
    queryFilters.push({
      key: 'userID',
      value: currentUserID,
      type: 'equal',
    });
  }

  console.log('selectedItemType', selectedItemType);

  const defaultFilters = !queryFilters.length
    ? ''
    : queryFilters.length === 1
      ? generateFilter(
          queryFilters[0].key,
          queryFilters[0].value,
          queryFilters[0].type
        )
      : generateFilterExpression('and', queryFilters);

  const queryArray = [
    generateSortQuery('modifiedAt', 'Decs'),
    defaultFilters,
    ...(pagination ? [generatePaginationQuery(pagination)] : []),
    ...(selectedItemType === 'shared' && currentUserID
      ? [generateCustomFilter('shared_with_userid', currentUserID)]
      : []),
  ];

  const url = generateURL(SPARK_RESOURCE_TYPES.BOARDS, queryArray);

  return fetchWrapper<BoardsResponse>({
    url: url,
    method: 'GET',
  });
};

type MutationVariables = {
  filters?: SparkFilterType;
  pagination?: PaginationQuery;
};

export function useBoards(
  variables?: MutationVariables,
  options?: QueryOptions
) {
  const { currentWorkspaceID } = useWorkspaceContext();
  const { currentUserID } = useCurrentUser();

  const response = useQuery(
    ['boards', currentWorkspaceID, variables],
    () => getBoards(variables, currentWorkspaceID, currentUserID),
    {
      onError: (error) => {
        if (error instanceof Error) {
          toastr.error(`Failed to fetch boards: ${error.message}`);
        }
      },
      ...CACHE_AND_STALE_TIME,
      ...options,
    }
  );

  const boards = response.data;
  const total = boards?.meta?.total || 0;
  return {
    ...response,
    boards,
    total,
  };
}
